import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Outlet, useNavigate, useLocation } from "react-router";
import { BottomNav } from "../components/BottomNav";
import { LiquidGlassFilter } from "../components/LiquidGlassFilter";
import { PatternBackground } from "../components/PatternBackground";
import { AppStoreProvider } from "../store/AppStoreProvider";
import { useAppStore } from "../store/useAppStore";
import { maxPlatform } from "../services/maxPlatform";
import { visitorAuthService } from "../services/visitorAuthService";
import { visitorSessionService } from "../services/visitorSessionService";
import { notificationService } from "../services/notificationService";
import { eventsService } from "../services/eventsService";
import { eventPackService } from "../services/eventPackService";
import { outboxService } from "../services/outboxService";
import { isApiEnabled } from "../api/config";
import { appConfig } from "../config";
import { ApiError } from "../api/client";
import { debugLog } from "../services/debugLog";
import { isStubAllowedPath, useEventStubMode } from "../hooks/useEventStubMode";
import { isPublicEntryPath, maxBotChatStartUrl } from "../utils/publicEntry";
import { visitorAuthApi } from "../api/auth";
import { LegalConsentGate } from "../components/LegalConsentGate";
import { MaxChatActivateGate } from "../components/MaxChatActivateGate";

const PAGES_WITH_NAV = [
  "/home",
  "/search",
  "/companies",
  "/products",
  "/map",
  "/my",
  "/chats",
  "/history",
  "/card",
  "/scan",
  "/events",
  "/schedule",
  "/favorites",
  // /guide and /support are full-screen chats — no bottom tab bar
  "/representatives",
  "/visitors",
];

function VisitorLegalConsentGate() {
  const [needed, setNeeded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!isApiEnabled() || !visitorAuthService.isLoggedIn()) {
      setNeeded(false);
      return;
    }
    if (location.pathname === "/login" || isPublicEntryPath(location.pathname)) {
      setNeeded(false);
      return;
    }
    let cancelled = false;
    void visitorAuthApi
      .me()
      .then((res) => {
        if (!cancelled) setNeeded(Boolean(res.user.needsLegalConsent));
      })
      .catch(() => {
        if (!cancelled) setNeeded(false);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (!needed) return null;
  return (
    <LegalConsentGate
      audience="visitor"
      onComplete={() => setNeeded(false)}
    />
  );
}

function VisitorSessionSync() {
  const { updateUser, selectedEventId, setEventRole } = useAppStore();

  useEffect(() => {
    if (!isApiEnabled() || !visitorAuthService.isLoggedIn()) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;

    visitorSessionService.syncProfile(updateUser).catch((err) => {
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 401) {
        debugLog.warn("auth", "syncProfile 401 → logout");
        visitorAuthService.logout();
        return;
      }
      debugLog.warn("auth", "syncProfile failed (keep session)", err);
    });
  }, [updateUser]);

  useEffect(() => {
    if (!isApiEnabled() || !visitorAuthService.isLoggedIn() || !selectedEventId) return;
    visitorSessionService.syncEventRole(selectedEventId, setEventRole).catch(() => undefined);
  }, [selectedEventId, setEventRole]);

  useEffect(() => {
    if (!isApiEnabled() || !visitorAuthService.isLoggedIn()) return;
    return visitorSessionService.startPresenceHeartbeat();
  }, []);

  return null;
}

function NotificationPoller() {
  useEffect(() => {
    if (!isApiEnabled() || !visitorAuthService.isLoggedIn()) return;
    return notificationService.startPolling();
  }, []);

  return null;
}

function OfflinePackSync() {
  const { selectedEventId } = useAppStore();
  const [localeTick, setLocaleTick] = useState(0);

  useEffect(() => {
    const onLocale = () => setLocaleTick((n) => n + 1);
    window.addEventListener("mvk:locale-changed", onLocale);
    return () => window.removeEventListener("mvk:locale-changed", onLocale);
  }, []);

  useEffect(() => {
    if (!appConfig.featureFlags.enableOfflineMode) return;
    if (!selectedEventId || !isApiEnabled() || !visitorAuthService.isLoggedIn()) return;
    void eventPackService.sync(selectedEventId);
    return outboxService.startAutoFlush();
  }, [selectedEventId, localeTick]);

  return null;
}

function RootInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { onboardingComplete, selectedEventId, navbarVisible, updateUser, selectEvent, completeOnboarding } =
    useAppStore();
  const stubMode = useEventStubMode();
  /** Bumped only on user language switch — remounts pages to refresh event-pack copy. */
  const [localeEpoch, setLocaleEpoch] = useState(0);
  const [maxAuthPending, setMaxAuthPending] = useState(
    () =>
      isApiEnabled() &&
      maxPlatform.isAvailable() &&
      !visitorAuthService.isLoggedIn(),
  );
  /** null = checking/ok; string = show gate with this deep link */
  const [chatGateLink, setChatGateLink] = useState<string | null>(null);
  const [dialogReady, setDialogReady] = useState(
    () => !(isApiEnabled() && maxPlatform.isAvailable() && !visitorAuthService.isLoggedIn()),
  );

  useEffect(() => {
    const onLocale = () => setLocaleEpoch((n) => n + 1);
    window.addEventListener("mvk:locale-changed", onLocale);
    return () => window.removeEventListener("mvk:locale-changed", onLocale);
  }, []);

  useEffect(() => {
    if (maxPlatform.isAvailable()) {
      maxPlatform.bootFullscreen();
    }
    maxPlatform.syncVisualViewportCss();

    const onVisible = () => {
      if (document.visibilityState === "visible") void maxPlatform.enterFullscreen();
    };
    // window.resize (orientation / sheet expand) — re-ask MAX for fullscreen.
    // Do NOT call enterFullscreen on visualViewport resize: that fights iOS keyboard
    // and leaves a white gap between form and keyboard in MAX WebView.
    const onWindowResize = () => {
      void maxPlatform.enterFullscreen();
    };
    const onVisualViewport = () => {
      maxPlatform.syncVisualViewportCss();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("resize", onWindowResize);
    window.visualViewport?.addEventListener("resize", onVisualViewport);
    window.visualViewport?.addEventListener("scroll", onVisualViewport);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("resize", onWindowResize);
      window.visualViewport?.removeEventListener("resize", onVisualViewport);
      window.visualViewport?.removeEventListener("scroll", onVisualViewport);
    };
  }, []);

  // Gate: require bot dialog before MAX bootstrap / login.
  useEffect(() => {
    if (!maxAuthPending || !isApiEnabled() || !maxPlatform.isAvailable()) {
      setDialogReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const initData = maxPlatform.getInitData();
      if (!initData) {
        if (!cancelled) setDialogReady(true);
        return;
      }
      try {
        const status = await visitorAuthApi.maxDialogStatus(initData, { probe: true });
        if (cancelled) return;
        if (!status.activated) {
          setChatGateLink(status.activateDeepLink || maxBotChatStartUrl("start"));
          return;
        }
      } catch (err) {
        debugLog.warn("auth", "dialog-status failed (fail-open)", err);
      }
      if (!cancelled) setDialogReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [maxAuthPending]);

  // Poll while gate is visible.
  useEffect(() => {
    if (!chatGateLink) return;
    let cancelled = false;
    const tick = async () => {
      const initData = maxPlatform.getInitData();
      if (!initData) return;
      try {
        const status = await visitorAuthApi.maxDialogStatus(initData, { probe: false });
        if (cancelled) return;
        if (status.activated) {
          setChatGateLink(null);
          setDialogReady(true);
        } else if (status.activateDeepLink) {
          setChatGateLink(status.activateDeepLink);
        }
      } catch {
        /* keep polling */
      }
    };
    const id = window.setInterval(() => void tick(), 2000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [chatGateLink]);

  useEffect(() => {
    if (!maxAuthPending || !dialogReady) return;

    let cancelled = false;
    (async () => {
      const fromQr = maxPlatform.resolveQrOpaqueFromStartParam();
      if (fromQr) {
        try {
          sessionStorage.setItem("mvk_pending_qr", fromQr);
        } catch {
          // ignore
        }
      }
      const ok = await visitorAuthService.tryMaxBootstrap(updateUser, fromQr ?? undefined);
      if (cancelled) return;

      if (ok) {
        eventsService.clearCache();
        const fromStart = maxPlatform.resolveEventIdFromStartParam();
        try {
          const { claimPendingQr } = await import("../services/publicQrFlow");
          const claimed = await claimPendingQr();
          if (claimed?.eventId) {
            selectEvent(claimed.eventId);
            completeOnboarding();
            navigate(claimed.navigateTo, { replace: true });
            setMaxAuthPending(false);
            return;
          }
        } catch {
          // fall through to normal launch
        }
        try {
          const resolved = await eventsService.resolveLaunchEvent(fromStart);
          if (resolved) {
            selectEvent(resolved.id);
            completeOnboarding();
            if (location.pathname === "/" || location.pathname === "/login") {
              navigate("/home", { replace: true });
            }
          }
        } catch {
          // keep picker if events cannot load yet
        }
      }
      setMaxAuthPending(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    maxAuthPending,
    dialogReady,
    updateUser,
    selectEvent,
    completeOnboarding,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    if (!isApiEnabled() || maxAuthPending) return;

    if (isPublicEntryPath(location.pathname)) {
      if (
        visitorAuthService.isLoggedIn() &&
        (location.pathname === "/login" ||
          location.pathname === "/start" ||
          location.pathname === "/go" ||
          location.pathname === "/")
      ) {
        navigate(
          onboardingComplete && selectedEventId ? "/home" : "/onboarding",
          { replace: true },
        );
      }
      return;
    }

    if (visitorAuthService.shouldRequireLogin() && location.pathname !== "/login") {
      // IDEA-041: unauthenticated users land on entry chooser, not login.
      navigate("/", { replace: true });
      return;
    }
  }, [
    location.pathname,
    navigate,
    maxAuthPending,
    onboardingComplete,
    selectedEventId,
  ]);

  useEffect(() => {
    if (maxAuthPending) return;
    if (isPublicEntryPath(location.pathname)) return;
    if (
      onboardingComplete &&
      selectedEventId &&
      (location.pathname === "/onboarding")
    ) {
      navigate("/home", { replace: true });
    }
  }, [maxAuthPending, onboardingComplete, selectedEventId, location.pathname, navigate]);

  useEffect(() => {
    if (isPublicEntryPath(location.pathname)) return;
    if (
      (!onboardingComplete || !selectedEventId) &&
      location.pathname !== "/onboarding" &&
      location.pathname !== "/login" &&
      location.pathname !== "/events"
    ) {
      navigate("/onboarding", { replace: true });
    }
  }, [onboardingComplete, selectedEventId, location.pathname, navigate]);

  // Event stub: deep links / catalog routes redirect to coming-soon home.
  useEffect(() => {
    if (!stubMode || !selectedEventId) return;
    if (isStubAllowedPath(location.pathname)) return;
    navigate("/home", { replace: true });
  }, [stubMode, selectedEventId, location.pathname, navigate]);

  // Hide nav on chat detail and onboarding
  const isChatDetail = /^\/chats\/.+$/.test(location.pathname);
  const showNav =
    !isChatDetail &&
    PAGES_WITH_NAV.some(
      (p) => location.pathname === p || (p !== "/home" && location.pathname.startsWith(p))
    );

  return (
    <>
      <VisitorSessionSync />
      <NotificationPoller />
      <OfflinePackSync />
      <Outlet key={localeEpoch || undefined} />
      <VisitorLegalConsentGate />
      {chatGateLink ? (
        <MaxChatActivateGate
          activateDeepLink={chatGateLink}
          onRecheck={async () => {
            const initData = maxPlatform.getInitData();
            if (!initData) return false;
            const status = await visitorAuthApi.maxDialogStatus(initData, { probe: true });
            if (status.activated) {
              setChatGateLink(null);
              setDialogReady(true);
              return true;
            }
            if (status.activateDeepLink) setChatGateLink(status.activateDeepLink);
            return false;
          }}
        />
      ) : null}
      {/* Always mounted — hide via prop so nav does not re-animate on route change */}
      {createPortal(
        <BottomNav stubMode={stubMode} hidden={!showNav || !navbarVisible} />,
        document.body,
      )}
    </>
  );
}

function RootShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  // Map / support chat need a definite height + flex column — otherwise flex-1
  // collapses in MAX WebView and keyboard / scroll fight the outer page scroller.
  const lockViewport =
    location.pathname === "/map" ||
    location.pathname.startsWith("/map/") ||
    location.pathname === "/scan" ||
    location.pathname.startsWith("/scan/") ||
    location.pathname === "/guide" ||
    location.pathname === "/support" ||
    location.pathname.startsWith("/support/");

  useEffect(() => {
    try {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
    } catch {
      // ignore
    }
  }, []);

  useLayoutEffect(() => {
    const reset = () => {
      const pane = document.querySelector<HTMLElement>("[data-app-scroll]");
      if (pane) pane.scrollTop = 0;
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    reset();
    const raf1 = requestAnimationFrame(() => {
      reset();
      requestAnimationFrame(reset);
    });
    const t1 = window.setTimeout(reset, 50);
    const t2 = window.setTimeout(reset, 250);
    return () => {
      cancelAnimationFrame(raf1);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [location.pathname, location.search, location.key]);

  return (
    <div
      // PWA: 100dvh. MAX: html.max-webapp sets --max-viewport-height; inherit via 100%.
      // Do not pin plain Safari/PWA to visualViewport — that clips chrome mid-screen.
      // app-shell-safe: one status-bar inset for all routes (headers no longer fight the notch).
      className="app-shell-safe max-w-lg mx-auto relative flex flex-col w-full overflow-hidden h-[100dvh] max-h-[100dvh] max-webapp:h-[var(--max-viewport-height,100dvh)] max-webapp:max-h-[var(--max-viewport-height,100dvh)]"
    >
      {/* Liquid glass: SVG filter + mesh backdrop (no z-index on content wrapper) */}
      <LiquidGlassFilter />
      <PatternBackground />
      {lockViewport ? (
        <div
          key={`lock-${location.pathname}`}
          className="relative flex flex-col flex-1 min-h-0 w-full h-full overflow-hidden"
        >
          {children}
        </div>
      ) : (
        <div
          key={`scroll-${location.pathname}`}
          data-app-scroll
          className="relative flex-1 min-h-0 w-full overflow-y-auto overscroll-y-contain touch-pan-y"
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function RootLayout() {
  return (
    <AppStoreProvider>
      <RootShell>
        <RootInner />
      </RootShell>
    </AppStoreProvider>
  );
}